import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { cognitoHostedUIBase } from '../authConfig';
import { talktreesCompleteFederatedUrl } from '../apiConfig';

const defaultTheme = createTheme();
const FEDERATED_SIGNUP_URL = talktreesCompleteFederatedUrl();

function parseHashFragment() {
  const hash = window.location.hash?.replace(/^#/, '') || '';
  const params = new URLSearchParams(hash);
  return {
    id_token: params.get('id_token'),
    access_token: params.get('access_token'),
    state: params.get('state'),
    error: params.get('error'),
    error_description: params.get('error_description'),
  };
}

export default function SignupCallback() {
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const hasProcessedCallback = useRef(false);

  useEffect(() => {
    if (hasProcessedCallback.current) {
      return;
    }
    hasProcessedCallback.current = true;

    const { id_token, state, error, error_description } = parseHashFragment();

    if (error) {
      /* global mixpanel */
      mixpanel.track('signupFailed', { method: 'Google', error });
      setStatus('error');
      const baseMessage =
        error_description || error || 'Google sign-up was cancelled or failed.';
      const hasInviteHint = /registration code|registrationCode|invite/i.test(baseMessage);
      setMessage(
        hasInviteHint
          ? baseMessage
          : `${baseMessage} Use Sign up again with a valid instructor registration code if you need an account.`
      );
      return;
    }

    if (!id_token) {
      setStatus('error');
      setMessage('Missing sign-up data. Start again from the sign-up page.');
      return;
    }

    (async () => {
      const givenName = sessionStorage.getItem('signup_given_name') || '';
      const familyName = sessionStorage.getItem('signup_family_name') || '';

      try {
        const res = await fetch(FEDERATED_SIGNUP_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            idToken: id_token,
            registrationCode: (state || '').trim(),
            givenName,
            familyName,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          // Some backend failures are non-fatal for users whose account was already created.
          if (res.status >= 500) {
            setStatus('success');
            setMessage('Your account appears to be created. Please Log in with Google to continue.');
            sessionStorage.removeItem('signup_given_name');
            sessionStorage.removeItem('signup_family_name');
            window.history.replaceState(null, '', window.location.pathname);
            mixpanel.track('userSignedUp', { method: 'Google' });
            return;
          }
          setStatus('error');
          setMessage(data.error || 'Could not complete sign-up.');
          /* global mixpanel */
          mixpanel.track('signupFailed', { step: 2, method: 'Google', error: data.error || 'Could not complete sign-up' });
          return;
        }
        sessionStorage.removeItem('signup_given_name');
        sessionStorage.removeItem('signup_family_name');
        mixpanel.track('userSignedUp', { method: 'Google' });
        setStatus('success');
        setMessage(
          data.message ? data.message.replace(/\bgoogle\b/g, 'Google') : 'Account ready. Log in with Google again to open the portal.'
        );
        window.history.replaceState(null, '', window.location.pathname);
      } catch {
        setStatus('error');
        setMessage('Network error. Check REACT_APP_API_BASE_URL and try again.');
        mixpanel.track('signupFailed', { step: 2, method: 'Google', error: 'Network error completing federated sign-up' });

      }
    })();
  }, []);

  return (
    <ThemeProvider theme={defaultTheme}>
      <Container component="main" maxWidth="xs">
        <Box sx={{ marginTop: 8, textAlign: 'center' }}>
          {status === 'loading' && (
            <>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Completing sign-up…</Typography>
            </>
          )}
          {status === 'error' && (
            <>
              <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                {message}
              </Alert>
              <Button component={RouterLink} to="/signup" variant="contained">
                Back to sign up
              </Button>
              <Typography variant="caption" display="block" sx={{ mt: 3, color: 'text.secondary' }}>
                Hosted UI domain: {cognitoHostedUIBase}
              </Typography>
            </>
          )}
          {status === 'success' && (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                {message}
              </Alert>
              <Button component={RouterLink} to="/login" variant="contained" sx={{ textTransform: 'none' }} fullWidth>
                Go to Log in
              </Button>
            </>
          )}
        </Box>
      </Container>
    </ThemeProvider>
  );
}
